import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Console je dovoljan u dev; prod monitoring će kupiti kroz uncaught handler
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <DefaultFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="rounded-lg border border-destructive bg-card p-6">
      <h2 className="text-lg font-semibold text-destructive">
        Greška u prikazu stranice
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Komponenta je pukla pri renderovanju. Probaj refresh ili se vrati na
        prethodnu stranicu.
      </p>
      <pre className="mt-3 max-h-40 overflow-auto rounded bg-muted p-2 text-xs">
        {error.message}
      </pre>
      <div className="mt-4 flex gap-2">
        <button
          onClick={reset}
          className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
        >
          Pokušaj ponovo
        </button>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
        >
          Reload stranice
        </button>
      </div>
    </div>
  );
}
